import type { Config as PmcConfig } from '@prime-types/pmcstarter'
import DatadogClient from '@prismamedia/datadog-client'
import type { BrandTrigram } from 'node_modules/@prismamedia/datadog-client/types/types'
import GlobalStore from 'shared/globalStore/assets/scripts/globalStore'
import PmcBridge from 'shared/services/pmc-bridge'

/**
 * Datadog
 * @module Datadog
 * @description This module initializes and configures the Datadog client for monitoring and analytics.
 */
export default class Datadog {
	private pmcBridge: PmcBridge
	private datadogClient?: DatadogClient
	private config?: PmcConfig
	private trigram?: string
	private store: GlobalStore

	/**
	 * Creates an instance of Datadog.
	 * @constructor
	 */
	constructor() {
		this.pmcBridge = new PmcBridge()
		this.store = new GlobalStore()
	}

	/**
	 * Initializes the Datadog module.
	 * @async
	 * @returns {Promise<void>}
	 * @description Fetches the configuration and trigram, then initializes the Datadog client.
	 */
	async init(): Promise<void> {
		this.config = await this.pmcBridge.getConfig()
		this.trigram = this.store.brand?.metadata.trigram ?? ''
		this.initDatadogClient()
	}

	/**
	 * Initializes the Datadog client.
	 * @private
	 * @returns {void}
	 * @description Configures and initializes the Datadog client with the appropriate settings.
	 */
	private initDatadogClient(): void {
		const env = this.config?.env ?? 'prod'
		const sessionSampleRate = this.store.brand?.third_party?.datadog?.session_sample_rate || 0

		if (!this.trigram) {
			console.error('OWS::datadog: No trigram found')
			return
		}

		this.datadogClient = new DatadogClient({
			brandTrigram: this.trigram as BrandTrigram,
			version: this.store.common?.codeVersion ?? '',
			sessionSampleRate: env.includes('rec') ? 100 : sessionSampleRate,
			sessionReplaySampleRate: 0,
			options: {
				defaultPrivacyLevel: 'mask-user-input',
				profilingSampleRate: 100,
				traceSampleRate: 0
			},
			useSlimSdk: false
		})

		this.datadogClient.initializeDatadogRUM()
		this.datadogClient.init()
		window.DD_RUM.onReady(() => {
			window.DD_RUM.setGlobalContextProperty('ows_site', true)
		})
	}
}
